/**
 * Activity Tracker Service
 * Logs user interactions for engagement analytics
 */
import http from '@/api/http';

class ActivityTracker {
  constructor() {
    this.buffer = [];
    this.bufferSize = 10;
    this.flushInterval = 30000; // 30 seconds
    this.sessionId = this.getOrCreateSessionId();

    // Start auto-flush timer
    setInterval(() => this.flush(), this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush());
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('pam_session_id');
    if (!sessionId) {
      sessionId = this.generateUUID();
      sessionStorage.setItem('pam_session_id', sessionId);
    }
    return sessionId;
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Safely serialize metadata, removing circular references
   */
  safeSerialize(obj) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    try {
      // Try to serialize - if it fails, return a simple object
      JSON.stringify(obj);
      return obj;
    } catch (e) {
      // Circular reference detected - extract only primitive values
      const safe = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const val = obj[key];
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
            safe[key] = val;
          } else if (val === null) {
            safe[key] = null;
          }
          // Skip objects, arrays, and functions to avoid circularity
        }
      }
      return safe;
    }
  }

  /**
   * Log an activity event
   * @param {string} activityType - Type of activity (e.g., 'dashboard_view', 'page_view')
   * @param {object} options - Additional options
   * @param {string} options.resourceType - Type of resource (e.g., 'dashboard', 'admin_page')
   * @param {number} options.resourceId - ID of the resource
   * @param {string} options.resourceName - Name of the resource
   * @param {object} options.metadata - Additional metadata
   */
  log(activityType, options = {}) {
    const activity = {
      activity_type: activityType,
      resource_type: options.resourceType || null,
      resource_id: options.resourceId || null,
      resource_name: options.resourceName || null,
      metadata: this.safeSerialize(options.metadata) || null
    };

    this.buffer.push(activity);

    // Auto-flush if buffer is full
    if (this.buffer.length >= this.bufferSize) {
      this.flush();
    }
  }

  /**
   * Log a dashboard view
   */
  logDashboardView(locationId, parkName) {
    this.log('dashboard_view', {
      resourceType: 'dashboard',
      resourceId: locationId,
      resourceName: parkName
    });
  }

  /**
   * Log a page view
   */
  logPageView(pageName, metadata = {}) {
    this.log('page_view', {
      resourceType: 'admin_page',
      resourceName: pageName,
      metadata
    });
  }

  /**
   * Log a button click / action
   */
  logAction(actionName, metadata = {}) {
    this.log('button_click', {
      resourceType: 'ui_interaction',
      resourceName: actionName,
      metadata
    });
  }

  /**
   * Flush buffered activities to server
   */
  async flush() {
    if (this.buffer.length === 0) {
      return;
    }

    const activities = [...this.buffer];
    this.buffer = [];

    try {
      // Send to server (don't await - fire and forget)
      await http.post('/api/v1/activity/batch', activities);
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('Activity tracking error:', error.message);
    }
  }
}

// Export singleton instance
const activityTracker = new ActivityTracker();
export default activityTracker;
